import React, { useState, useEffect } from "react";
import { atividades, clientes, usuarios } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge, PriorityBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Pencil, Trash2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export function AtividadesPage() {
  const [data, setData] = useState([]);
  const [clientesList, setClientesList] = useState([]);
  const [usuariosList, setUsuariosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [prioridadeFilter, setPrioridadeFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    categoria_atividade: "",
    prioridade: "media",
    responsavel_usuario_id: "",
    responsavel_nome: "",
    prazo: "",
    cliente_id: "",
    cliente_nome: "",
    observacoes: "",
  });

  const fetchData = async () => {
    try {
      const [ativsRes, clientesRes, usuariosRes] = await Promise.all([
        atividades.list(),
        clientes.list(),
        usuarios.list(),
      ]);
      setData(ativsRes.data);
      setClientesList(clientesRes.data);
      setUsuariosList(usuariosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar atividades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await atividades.update(editingItem.id, formData);
        toast.success("Atividade atualizada!");
      } else {
        await atividades.create(formData);
        toast.success("Atividade criada!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      titulo: item.titulo || "",
      descricao: item.descricao || "",
      categoria_atividade: item.categoria_atividade || "",
      prioridade: item.prioridade || "media",
      responsavel_usuario_id: item.responsavel_usuario_id || "",
      responsavel_nome: item.responsavel_nome || "",
      prazo: item.prazo || "",
      cliente_id: item.cliente_id || "",
      cliente_nome: item.cliente_nome || "",
      observacoes: item.observacoes || "",
    });
    setIsDialogOpen(true);
  };

  const handleConcluir = async (id) => {
    try {
      await atividades.update(id, { status: "concluida" });
      toast.success("Atividade concluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao concluir atividade");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta atividade?")) return;
    try {
      await atividades.delete(id);
      toast.success("Atividade excluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientesList.find((c) => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente?.razao_social || "",
    });
  };

  const handleUsuarioChange = (usuarioId) => {
    const usuario = usuariosList.find((u) => u.id === usuarioId);
    setFormData({
      ...formData,
      responsavel_usuario_id: usuarioId,
      responsavel_nome: usuario?.nome || "",
    });
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      titulo: "",
      descricao: "",
      categoria_atividade: "",
      prioridade: "media",
      responsavel_usuario_id: "",
      responsavel_nome: "",
      prazo: "",
      cliente_id: "",
      cliente_nome: "",
      observacoes: "",
    });
  };

  const filteredData = data.filter((item) => {
    const matchesSearch = item.titulo?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesPrioridade = prioridadeFilter === "all" || item.prioridade === prioridadeFilter;
    return matchesSearch && matchesStatus && matchesPrioridade;
  });

  // Stats
  const abertas = data.filter((a) => ["aberta", "em_andamento", "aguardando_terceiro"].includes(a.status)).length;
  const hoje = new Date().toISOString().split("T")[0];
  const atrasadas = data.filter((a) => a.prazo && a.prazo < hoje && a.status !== "concluida").length;
  const urgentes = data.filter((a) => a.prioridade === "urgente" && a.status !== "concluida").length;

  const columns = [
    { key: "titulo", header: "Título" },
    { key: "categoria_atividade", header: "Categoria" },
    { key: "prioridade", header: "Prioridade", render: (v) => <PriorityBadge priority={v} /> },
    { key: "responsavel_nome", header: "Responsável" },
    { key: "cliente_nome", header: "Cliente" },
    { key: "prazo", header: "Prazo", render: (v) => formatDate(v) },
    { key: "status", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "120px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1">
            {row.status !== "concluida" && (
              <Button variant="ghost" size="icon" onClick={() => handleConcluir(row.id)} title="Concluir">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => handleEdit(row)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="atividades-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Atividades Operacionais</h1>
          <p className="text-muted-foreground">Gerencie as atividades e tarefas da equipe</p>
        </div>
        {isAdmin() && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-atividade-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova Atividade
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingItem ? "Editar Atividade" : "Nova Atividade"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                    data-testid="atividade-titulo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    data-testid="atividade-descricao"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select value={formData.categoria_atividade} onValueChange={(v) => setFormData({ ...formData, categoria_atividade: v })}>
                      <SelectTrigger data-testid="atividade-categoria">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="follow_up">Follow-up Cliente</SelectItem>
                        <SelectItem value="cobranca">Cobrança</SelectItem>
                        <SelectItem value="conferencia">Conferência Documental</SelectItem>
                        <SelectItem value="importacao">Acompanhamento Importação</SelectItem>
                        <SelectItem value="entrega">Entrega</SelectItem>
                        <SelectItem value="fornecedor">Tratativa Fornecedor</SelectItem>
                        <SelectItem value="estoque">Atualização Estoque</SelectItem>
                        <SelectItem value="pos_venda">Pós-venda</SelectItem>
                        <SelectItem value="financeiro">Pendência Financeira</SelectItem>
                        <SelectItem value="operacional">Pendência Operacional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prioridade</Label>
                    <Select value={formData.prioridade} onValueChange={(v) => setFormData({ ...formData, prioridade: v })}>
                      <SelectTrigger data-testid="atividade-prioridade">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baixa">Baixa</SelectItem>
                        <SelectItem value="media">Média</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="urgente">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Responsável</Label>
                    <Select value={formData.responsavel_usuario_id} onValueChange={handleUsuarioChange}>
                      <SelectTrigger data-testid="atividade-responsavel">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {usuariosList.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prazo</Label>
                    <Input
                      type="date"
                      value={formData.prazo}
                      onChange={(e) => setFormData({ ...formData, prazo: e.target.value })}
                      data-testid="atividade-prazo"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={formData.cliente_id} onValueChange={handleClienteChange}>
                    <SelectTrigger data-testid="atividade-cliente">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientesList.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    data-testid="atividade-obs"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-atividade-btn">{editingItem ? "Salvar" : "Criar"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Abertas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold">{abertas}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Atrasadas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold text-red-600">{atrasadas}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Urgentes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-heading font-bold text-orange-600">{urgentes}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-atividades"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status-atividades">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="aberta">Aberta</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="aguardando_terceiro">Aguardando</SelectItem>
            <SelectItem value="concluida">Concluída</SelectItem>
          </SelectContent>
        </Select>
        <Select value={prioridadeFilter} onValueChange={setPrioridadeFilter}>
          <SelectTrigger className="w-40" data-testid="filter-prioridade">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="baixa">Baixa</SelectItem>
            <SelectItem value="media">Média</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="urgente">Urgente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhuma atividade encontrada" />
    </div>
  );
}
